import { observer } from "mobx-react";
import * as React from "react";
import { t } from "../../../i18n/util";
import { API } from "../../../network/API";
import { generalStore } from "../../../stores/GeneralStore";
import { hrStore } from "../../../stores/HrStore";
import { DocumentsEnum } from "../../../types/models";
import { useMobileMediaQuery } from "../../hooks/useMobileMediaQuery";
import { AddField } from "../../ui/AddField";
import { ApplicantFileField } from "../../ui/ApplicantFileField";
import { ApplicantLinkField } from "../../ui/ApplicantLinkField";
import { ConfirmationDialog } from "../../ui/ConfirmationDialog";

export const ApplicantDocumentsSite = observer(() => {
    const [deleteId, setDeleteId] = React.useState<string | undefined>(undefined);
    const [selectedDocument, setSelectedDocument] = React.useState<DocumentsEnum>("optionalFiles");
    const [type, setType] = React.useState<"file" | "link" | undefined>(undefined);
    const isMobile = useMobileMediaQuery();

    const handleAddLink = async (link: string) => {
        try {
            generalStore.isLoading = true;
            let optionalLinks = [link];
            if (!!hrStore.currentApplicant.optionalLinks) {
                optionalLinks = [...hrStore.currentApplicant.optionalLinks, link];
            }

            await API.postOptionalLinks(hrStore.currentApplicantID, {
                optionalLinks: optionalLinks
            });
            await hrStore.loadApplicant();
        } catch (error) {
            generalStore.setError(t("error.uploadLink"));
        } finally {
            generalStore.isLoading = false;
        }
    };

    const handleAddFile = async (file: File) => {
        try {
            generalStore.isLoading = true;
            if (selectedDocument === "optionalFiles") {
                await API.patchApplicant(hrStore.currentApplicantID, { optionalFiles: [file] });
            } else {
                await API.patchApplicant(hrStore.currentApplicantID, { [selectedDocument]: file });
            }
            await hrStore.loadApplicant();
        } catch (error) {
            generalStore.setError(t("error.uploadDocument"));
        } finally {
            generalStore.isLoading = false;
        }
    };

    const handleCloseDeleteDialog = () => {
        setDeleteId(undefined);
    };

    const handleSubmitDeleteDialog = async () => {
        if (!deleteId) {
            generalStore.setError(t("error.deleteDocument"));
            return;
        }
        try {
            generalStore.isLoading = true;
            if (type === "file") {
                await API.deleteDocument(hrStore.currentApplicantID, deleteId);
            } else if (type === "link") {
                await API.postOptionalLinks(hrStore.currentApplicantID, {
                    optionalLinks: [
                        ...(hrStore.currentApplicant.optionalLinks?.slice(0, Number(deleteId)) as string[]),
                        ...(hrStore.currentApplicant.optionalLinks?.slice(Number(deleteId) + 1) as string[])
                    ]
                });
            }

            await hrStore.loadApplicant();
        } catch (error) {
            generalStore.setError(t("error.deleteDocument"));
        } finally {
            setDeleteId(undefined);
            generalStore.isLoading = false;
        }
    };

    return (
        <div style={{ gap: 16, display: "flex", flexDirection: "column" }}>
            {hrStore.currentApplicant.resume && (
                <ApplicantFileField
                    url={hrStore.currentApplicant.resume.url}
                    documentName={hrStore.currentApplicant.resume.name ?? ""}
                    documentSize={hrStore.currentApplicant.resume.size ?? 0}
                    onDelete={() => {
                        setType("file");
                        setDeleteId(hrStore.currentApplicant.resume?.id);
                    }}
                />
            )}
            {hrStore.currentApplicant.video && (
                <ApplicantFileField
                    url={hrStore.currentApplicant.video.url}
                    documentName={hrStore.currentApplicant.video.name ?? ""}
                    documentSize={hrStore.currentApplicant.video.size ?? 0}
                    video
                    onDelete={() => {
                        setType("file");
                        setDeleteId(hrStore.currentApplicant.video?.id);
                    }}
                />
            )}
            {hrStore.currentApplicant.portfolio && (
                <ApplicantFileField
                    url={hrStore.currentApplicant.portfolio.url}
                    documentName={hrStore.currentApplicant.portfolio.name ?? ""}
                    documentSize={hrStore.currentApplicant.portfolio.size ?? 0}
                    onDelete={() => {
                        setType("file");
                        setDeleteId(hrStore.currentApplicant.portfolio?.id);
                    }}
                />
            )}
            {hrStore.currentApplicant.photo && (
                <ApplicantFileField
                    url={hrStore.currentApplicant.photo.url}
                    documentName={hrStore.currentApplicant.photo.name ?? ""}
                    documentSize={hrStore.currentApplicant.photo.size ?? 0}
                    image
                    onDelete={() => {
                        setType("file");
                        setDeleteId(hrStore.currentApplicant.photo?.id);
                    }}
                />
            )}
            {hrStore.currentApplicant.motivationalLetter && (
                <ApplicantFileField
                    url={hrStore.currentApplicant.motivationalLetter.url}
                    documentName={hrStore.currentApplicant.motivationalLetter.name ?? ""}
                    documentSize={hrStore.currentApplicant.motivationalLetter.size ?? 0}
                    onDelete={() => {
                        setType("file");
                        setDeleteId(hrStore.currentApplicant.motivationalLetter?.id);
                    }}
                />
            )}
            {hrStore.currentApplicant.contract && (
                <ApplicantFileField
                    url={hrStore.currentApplicant.contract.url}
                    documentName={hrStore.currentApplicant.contract.name ?? ""}
                    documentSize={hrStore.currentApplicant.contract.size ?? 0}
                    onDelete={() => {
                        setType("file");
                        setDeleteId(hrStore.currentApplicant.contract?.id);
                    }}
                />
            )}
            {hrStore.currentApplicant.optionalFiles?.map((optionalFile) => (
                <ApplicantFileField
                    key={optionalFile.id}
                    url={optionalFile.url}
                    documentName={optionalFile.name ?? ""}
                    documentSize={optionalFile.size ?? 0}
                    onDelete={() => {
                        setType("file");
                        setDeleteId(optionalFile.id);
                    }}
                />
            ))}
            {hrStore.currentApplicant.optionalLinks?.map((link, index) => (
                <ApplicantLinkField
                    link={link}
                    onDelete={() => {
                        setType("link");
                        setDeleteId(index.toString());
                    }}
                    key={index}
                />
            ))}
            {hrStore.currentApplicant.codingSubmissions?.map((submission, index) => (
                <ApplicantFileField
                    key={index}
                    url={submission.url}
                    documentName={submission.name ?? ""}
                    documentSize={submission.size ?? 0}
                    uploadDate={submission.createdAt}
                    notes={submission.notes}
                    downloadOnly
                />
            ))}
            <AddField
                addFile={handleAddFile}
                addLink={handleAddLink}
                sm={12}
                selectedDocument={selectedDocument}
                setSelectedDocument={setSelectedDocument}
                style={{ marginBottom: isMobile ? 55 : 80 }}
            />
            <ConfirmationDialog
                label={t("screen.applicantDocuments.delete.text")}
                isOpen={!!deleteId}
                onBack={handleCloseDeleteDialog}
                onSubmit={handleSubmitDeleteDialog}
            />
        </div>
    );
});
